import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import Typography from '@mui/material/Typography';
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Box from '@mui/material/Box';
import Grid from "@mui/material/Unstable_Grid2";
import { IconButton, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import {useTheme} from '@mui/material/styles';
import Select from '@mui/material/Select';
import { Container } from '@mui/system';
// components
import { fCurrencyEuro, fPercentEuro } from '../../utils/format-number';
import Iconify from '../../components/iconify';
import { useSettingsContext } from '../../components/settings';

import FinancialWidgetSummary from "./financial-widget-summary";
import { getFinancialData } from '../../components/upload';
import AnalyticsWidgetSummary from './analytics-widget-summary';
import ChartPiledBars from './charts/chart-piled-bars';
import ClickableTooltip from '../../components/tooltip/clickable-tooltip';

const abonoOptions = [
  { label: 'Total', value: 'abono' },
  { label: 'Duro', value: 'abono_duro' },
  { label: 'Movimientos', value: 'abono_movimientos' },
  { label: 'Blando', value: 'abono_blando' },
  { label: 'Extraordinarios', value: 'abono_extraordinarios' },
];

const abonoSubOptions = [ 
  { label: 'Trabajo', value: 'abono_trabajo', agrup: 'Duro' },
  { label: 'Pensión', value: 'abono_pension', agrup: 'Duro' },
  { label: 'Subsidio', value: 'abono_subsidio', agrup: 'Duro' },
  { label: 'Ahorro', value: 'abono_ahorro_duro', agrup: 'Duro' },
  { label: 'Facturación', value: 'abono_facturacion', agrup: 'Duro' },
  { label: 'Devolución' , value: 'abono_devolucion', agrup: 'Blando' },
  { label: 'Anulación', value: 'abono_anulacion', agrup: 'Blando' },
  { label: 'Gambling', value: 'abono_gambling_blando', agrup: 'Blando' },
  { label: 'Resto', value: 'abono_resto', agrup: 'Blando' },
  { label: 'Efectivo', value: 'abono_efectivo', agrup: 'Movimientos' },
  { label: 'Transferencias', value: 'abono_transferencias', agrup: 'Movimientos' },
  { label: 'Traspaso', value: 'abono_traspaso', agrup: 'Movimientos' },
  { label: 'Financiación', value: 'abono_financiacion', agrup: 'Extraordinarios' },
  { label: 'Impuestos', value: 'abono_impuestos', agrup: 'Extraordinarios' },
]

const gastoOptions = [
    { label: 'Total', value: 'gasto' },
    { label: 'Básico', value: 'gasto_basico' },
    { label: 'Movimientos', value: 'gasto_movimientos' },
    { label: 'Discrecional', value: 'gasto_discrecional' },
    { label: 'Extraordinarios', value: 'gasto_extraordinarios' },
];

const gastoSubOptions = [
    { label: 'Consumo indispensable', value: 'gasto_consumo_basico', agrup: 'Básico' },
    { label: 'Recibos', value: 'gasto_recibo', agrup: 'Básico' },
    { label: 'Comisiones', value: 'gasto_comisiones', agrup: 'Básico' },
    { label: 'Impuestos', value: 'gasto_impuestos', agrup: 'Básico' },
    { label: 'Consumo variado', value: 'gasto_consumo_discrecional', agrup: 'Discrecional' },
    { label: 'Tarjeta de credito', value: 'gasto_tarjetadecredito', agrup: 'Discrecional' },
    { label: 'Gastos TPV', value: 'gasto_tpv', agrup: 'Discrecional' },
    { label: 'Gambling', value: 'gasto_gambling', agrup: 'Discrecional' },
    { label: 'Resto', value: 'gasto_resto', agrup: 'Discrecional' },
    { label: 'Efectivo', value: 'gasto_efectivo', agrup: 'Movimientos' },
    { label: 'Transferencias', value: 'gasto_transferencias', agrup: 'Movimientos' },
    { label: 'Traspaso', value: 'gasto_traspaso', agrup: 'Movimientos' },
    { label: 'Financiación', value: 'gasto_financiacion', agrup: 'Extraordinarios' },
    { label: 'Recobro', value: 'gasto_recobro', agrup: 'Extraordinarios' },
    { label: 'Embargo', value: 'gasto_embargo', agrup: 'Extraordinarios' },
];


export default function FinancialIncomeExpenses() {
    const navigate = useNavigate();
    const settings = useSettingsContext()
    const theme = useTheme();
    
    const [resumen, setResumen] = useState(null);
    const [selectedAbono, setSelectedAbonoState] = useState('abono');
    const [selectedAbonoHistorico, setSelectedAbonoHistoricoState] = useState('abono')
    const [selectedGasto, setSelectedGastoState] = useState('gasto');
    const [selectedGastoHistorico, setSelectedGastoHistoricoState] = useState('gasto')

    useEffect(() => {
        try {
        const data = getFinancialData();
        if (!data) throw new Error('No data');
        setResumen(data);
        settings.onFiles(); // Solo si tienes datos
        settings.onValid();
        } catch (error) {
        console.warn('No se encontraron datos. Redirigiendo al inicio...');
        navigate('/financial');
        }
    }, [navigate, settings]);

    if (!resumen) return null;

    const rLastIndex = resumen.fecha.length - 1;

    const abonoHistoricoCategoriesGraphData = resumen.fecha.map(date => ({
        category: date
    }))
    let abonoHistoricoGraphData
    if (selectedAbonoHistorico==="abono"){
        abonoHistoricoGraphData = abonoOptions
            .filter(option => option.label !== 'Total')
            .map(option => ({
                name: option.label,
                data: resumen.abono.historico.map((entry) => (
                    entry[option.value]
                ))
            }))
    } else {
        const abonoHistoricoSelectedLabel = abonoOptions.find(option => option.value === selectedAbonoHistorico).label
        abonoHistoricoGraphData = abonoSubOptions
            .filter(option => option.agrup === abonoHistoricoSelectedLabel)
            .map(option => ({
                name: option.label,
                data: resumen.abono.historico.map((entry) => (
                    entry[option.value]
                ))
            }))
    }

    const gastoHistoricoCategoriesGraphData = resumen.fecha.map(date => ({
        category: date
    }))
    let gastoHistoricoGraphData
    if (selectedGastoHistorico==="gasto"){
        gastoHistoricoGraphData = gastoOptions
            .filter(option => option.label !== 'Total')
            .map(option => ({
                name: option.label,
                data: resumen.gasto.historico.map((entry) => (
                    -entry[option.value]
                ))
            }))
    } else {
        const gastoHistoricoSelectedLabel = gastoOptions.find(option => option.value === selectedGastoHistorico).label
        gastoHistoricoGraphData = gastoSubOptions
            .filter(option => option.agrup === gastoHistoricoSelectedLabel)
            .map(option => ({
                name: option.label,
                data: resumen.gasto.historico.map((entry) => (
                    -entry[option.value]
                ))
            }))
    }

    const handleSelectAbonoHistorico = (event) => {
        setSelectedAbonoHistoricoState(event.target.value);
    };

    const handleSelectGastoHistorico = (event) => {
        setSelectedGastoHistoricoState(event.target.value);
    };
    
    return (
        <Container maxWidth='xl'>
            <Stack spacing={3}>
                <Grid container spacing={3}>
                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                            title="Promedio mensual de ingresos"
                            tooltipText='Últimos 6 meses'
                            total={Math.floor(resumen.abono.abono) || '0'}
                            funct={fCurrencyEuro}
                            icon={<Iconify icon="mdi:cash-plus" width={64} />}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                            title="Estabilidad de ingresos"
                            tooltipText='Indica cómo de recurrente son los ingresos en los últimos 6 meses.  (Abonos duros / Abono total)'
                            total={Math.floor(100 * resumen.abono.estabilidad_abono) || '0'}
                            funct={fPercentEuro}
                            icon={<Iconify icon="mdi:chart-box-outline" width={64} />}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                            title="Promedio mensual de gastos"
                            tooltipText='Últimos 6 meses'
                            total={Math.floor(resumen.gasto.gasto) || '0'}
                            funct={fCurrencyEuro}
                            icon={<Iconify icon="mdi:cash-minus" width={64} />}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                            title="Estabilidad de gastos"
                            tooltipText='Indica cómo de recurrentes y predecibles son los gastos de los últimos 6 meses.  (Gastos básicos / Gasto total)'
                            total={Math.floor(100 * resumen.gasto.estabilidad_gasto_basico_tot_gasto) || '0'}
                            funct={fPercentEuro}
                            icon={<Iconify icon="mdi:chart-bell-curve" width={64} />}
                        />
                    </Grid>
                    
                </Grid>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <Card sx={{ flex: 1 }}>
                        <FinancialWidgetSummary
                            title="Ingresos últimos 30 días"
                            icon="mdi:trending-up"
                            percent={(resumen.ultimo_mes.ingreso_30_dias_actual - resumen.ultimo_mes.ingreso_30_dias_previo) / resumen.ultimo_mes.ingreso_30_dias_previo * 100}
                            total={Math.floor(resumen.ultimo_mes.ingreso_30_dias_actual) || '0'}
                        />
                    </Card>
                    <Card sx={{ flex: 1 }}>
                        <FinancialWidgetSummary
                            title="Gastos últimos 30 días"
                            icon="mdi:trending-down"
                            percent={(resumen.ultimo_mes.gasto_30_dias_actual - resumen.ultimo_mes.gasto_30_dias_previo) / resumen.ultimo_mes.gasto_30_dias_previo * 100}
                            total={Math.floor(Math.abs(resumen.ultimo_mes.gasto_30_dias_actual)) || '0'}
                        />
                    </Card>
                    <Card sx={{ flex: 1 }}>
                        <FinancialWidgetSummary
                            title="Capacidad de pago últimos 30 días"
                            icon="mdi:wallet-outline"
                            percent={(resumen.ultimo_mes.cdp_30_dias_actual - resumen.ultimo_mes.cdp_30_dias_previo) / resumen.ultimo_mes.cdp_30_dias_previo * 100}
                            total={Math.floor(resumen.ultimo_mes.cdp_30_dias_actual) || '0'}
                        />
                    </Card>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ width: '100%' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                            <Stack alignItems= 'left' marginTop={3}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h6">Agrupación</Typography>
                                    <ClickableTooltip title={
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                DURO:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Ingresos recurrentes y estables a lo largo del tiempo, como nóminas, pensiones o subsidios
                                                </Typography>
                                            </Typography>

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                MOVIMIENTOS:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Ingresos en efectivo, transferencias entrantes o traspasos recibidos
                                                </Typography>
                                            </Typography>

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                BLANDO:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Ingresos no recurrentes o esporádicos, como devoluciones, ingresos por premios o comisiones
                                                </Typography>
                                            </Typography>

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                RESTO:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Abonos de préstamos, disposiciones de crédito o ingresos por devolución de impuestos
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    } arrow>
                                    <IconButton component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <Iconify icon="mdi:information-outline" width={18} height={18} color={theme.palette.text.secondary} />
                                    </IconButton>
                                    </ClickableTooltip>
                                </Box>
                                <Select
                                value={selectedAbonoHistorico}
                                onChange={handleSelectAbonoHistorico}
                                displayEmpty
                                sx={{ minWidth: 160, maxWidth: 200 }}
                                >
                                {abonoOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                                </Select>
                            </Stack>
                            <ChartPiledBars
                                sx={{ width: '100%', marginTop:3 }}
                                color={theme.palette.simplai.array}
                                title="Evolución de los ingresos"
                                chart={{
                                    categories: abonoHistoricoCategoriesGraphData.map(entry => (entry.category)),
                                    series: abonoHistoricoGraphData.map(entry => ({
                                                name: entry.name,
                                                data: entry.data
                                            }))
                                }}
                            />
                            
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                            
                            <Stack alignItems= 'left' marginTop={3}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h6">Agrupación</Typography>
                                    <ClickableTooltip title={
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                BÁSICO:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Gastos indispensables como vivienda, alimentación o salud, además de recibos, comisiones e impuestos
                                                </Typography>
                                            </Typography>

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                MOVIMIENTOS:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Disposiciones en efectivo, transferencias salientes o traspasos enviados
                                                </Typography>
                                            </Typography>

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                DISCRECIONAL:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Gastos no indispensables como ocio, restaurantes o compras personales
                                                </Typography>
                                            </Typography>

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                RESTO:{' '}
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'normal' }}>
                                                Amortizaciones de préstamos u otros compromisos financieros, movimientos de recobro y embargos
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    } arrow>
                                    <IconButton component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <Iconify icon="mdi:information-outline" width={18} height={18} color={theme.palette.text.secondary} />
                                    </IconButton>
                                    </ClickableTooltip>
                                </Box>
                                <Select
                                value={selectedGastoHistorico}
                                onChange={handleSelectGastoHistorico}
                                displayEmpty
                                sx={{ minWidth: 160, maxWidth: 200 }}
                                >
                                {gastoOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                    </MenuItem>
                                ))}
                                </Select>
                            </Stack>
                            <ChartPiledBars
                                sx={{ width: '100%', marginTop: 3}}
                                color={theme.palette.simplai.array}
                                title="Evolución de los gastos"
                                chart={{
                                    categories: gastoHistoricoCategoriesGraphData.map(entry => (entry.category)),
                                    series: gastoHistoricoGraphData.map(entry => ({
                                                name: entry.name,
                                                data: entry.data
                                            }))
                                }}
                            />
                            
                        </Box>
                </Stack>
            </Stack>
        </Container>
    )
}